import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DocumentDtoKeysEnum } from '@zaoblako/shared/modules/document/enums/document-dto-keys.enum';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { BusinessCheckerKeysEnum } from '@zaoblako/shared/modules/business/enum/business-checker-keys.enum';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { DocumentFileTemplateFindInterface } from '@zaoblako/shared/modules/catalog/dtos/document-file-template/document-file-template-find.dto';

import * as yup from 'yup';

/** Contract update request body */
export interface ContractFormInterface {
  /** Contract number */
  number: string;
  /** Contract display name */
  name: string;
  /** Contract expiration date and time */
  expiresAt?: Date;
  /** Related company */
  company: CompanyFindInterface;
  /** Related counterparty */
  contragent: ContragentFindInterface;
  /** Related direction */
  direction?: DirectionFindInterface;
  /** Related unit */
  unit?: UnitFindInterface;
  /** Related filial */
  filial?: FilialFindInterface;
  /** Related cost center */
  costCenter?: CostCenterFindInterface;
  /** Related deal */
  deal?: DealFindInterface;
  /** Contract beginning date */
  date?: Date;
  /** If `true` then related documents will be excluded from analytics */
  ignoreInAnalytics?: boolean;
  /** Intergroup turnover flag, used as an additional filter in some contract-related reports */
  intergroupTurnover?: boolean;
  /** Related document file template */
  documentFileTemplate?: DocumentFileTemplateFindInterface;
}

/** Contract update request body - DTO schema */
export const ContractFormDto = SerdeDtoClass.create<ContractFormInterface>(DocumentDtoKeysEnum.CONTRACT_FORM_DTO, {
  number: {
    label: 'Номер',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  name: {
    label: 'Название',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  expiresAt: {
    optional: true,
    sortable: true,
    label: 'Срок действия',
    serializer: yup.date(),
  },
  company: {
    label: 'Компания',
    checkers: [ClientCheckerKeysEnum.COMPANY_ASSERT_EXISTS_BY_DTO],
    serializer: entityLikeSchema,
  },
  contragent: {
    label: 'Контрагент',
    checkers: [ClientCheckerKeysEnum.CONTRAGENT_ASSERT_EXISTS_BY_DTO],
    serializer: entityLikeSchema,
  },
  direction: {
    optional: true,
    label: 'БС',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
  },
  unit: {
    optional: true,
    // optional: context => !context?.data?.requiredAnalyticalFeatures?.unitsRequired,
    label: 'БЕ',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.UNIT_ASSERT_EXISTS_BY_DTO],
  },
  filial: {
    optional: true,
    // optional: context => !context?.data?.requiredAnalyticalFeatures?.filialsRequired,
    label: 'Филиал',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
  },
  costCenter: {
    optional: true,
    label: 'Центр затрат',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.COST_CENTER_ASSERT_EXISTS_BY_DTO],
  },
  deal: {
    sortable: true,
    optional: true,
    label: 'Сделка',
    serializer: entityLikeSchema,
    checkers: [BusinessCheckerKeysEnum.DEAL_ASSERT_EXISTS_BY_DTO],
  },
  date: {
    label: 'Дата начала договора',
    optional: true,
    serializer: yup.date(),
  },
  ignoreInAnalytics: {
    optional: true,
    label: 'Не учитывать документы в аналитике',
    serializer: yup.boolean(),
  },
  intergroupTurnover: {
    optional: true,
    label: 'Межгрупповой оборот',
    serializer: yup.boolean(),
  },
  documentFileTemplate: {
    optional: true,
    label: 'Шаблон документа',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.DOCUMENT_FILE_TEMPLATE_ASSERT_EXISTS_BY_DTO],
  },
});
